// The brand the application wears (ZENITH.md §18, #389): what /me resolved
// for this person, applied to the shell's mark, the document title, the
// favicon, the accent tokens and — where the brand sets them — its face pair and
// its palette (ADR-0242), and taken off again when they sign out.
//
// The brand is read once with the session and never per scope: a shell that
// changes its name as you move between sites is not a brand.
//
// **Before a session the brand comes from the host** (ADR-0065; #505), which
// `shell/hostbrand.tsx` fetches. Everything here — the tokens, the title, the
// favicon, the mark, the lockup, the footer — is the same rendering for both,
// and the one thing that differs is where a file is read from: a signed-in
// caller reads the `me/brand` paths, resolved from their memberships, and a page
// with no session reads the `brand` paths, resolved from the host. That is the
// `BrandSource` argument below, and it exists so there is one renderer rather
// than two that drift.
//
// **The title carries one thing the brand does not decide** (#963): where the
// deployment is not production, the word it calls itself goes in front of the
// name. That comes from `shell/deployment.ts` and is passed in, so this file
// stays a function of its arguments — see `applyBrand`.
import { useEffect, useRef, useState } from "react";
import type { components } from "../api/schema";
import { titleWith, useDeploymentLabel, type DeploymentLabel } from "./deployment";

export type Brand = components["schemas"]["Brand"];

/** The document title while nobody is signed in: neutral on purpose. */
export const SIGN_IN_TITLE = "Sign in";

/** The three tokens an accent overrides, in both themes (§18). The semantic
 * RF colours are not among them. */
const ACCENT_TOKENS = ["--accent", "--accent-ink", "--accent-soft"] as const;

/** The two face tokens a brand's typeface pair overrides (ADR-0242 decision 1a). */
const FACE_TOKENS = ["--font", "--mono"] as const;

/** The faces a brand may name, by key, and the stack each wears: the face
 * first, then the product's own stack behind it while the file loads or for a
 * glyph it lacks. The keys are internal/brand.Faces and MonoFaces, and the
 * files are theme.css's @font-face set. A key not here is the product's face,
 * never an error (decision 3b). */
export const FACE_STACKS: Record<string, string> = {
  "plex-sans": '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "open-sans": '"Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "inter": '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "figtree": '"Figtree", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "outfit": '"Outfit", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "montserrat": '"Montserrat", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "poppins": '"Poppins", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "lato": '"Lato", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  "roboto": '"Roboto", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
};
export const MONO_STACKS: Record<string, string> = {
  "plex-mono": '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace',
};

/** The five base halves a palette sets per theme (decision 2a), under
 * theme.css's names: paper is --bg, muted is --ink-2. theme.css derives the
 * rest of a marked theme from these. */
const PALETTE_ROLES = { paper: "--bg", surface: "--surface", ink: "--ink", muted: "--ink-2", line: "--line" } as const;
const THEMES = { light: "-l", dark: "-d" } as const;

/** Every custom property applyBrand may write. What is not here never moves
 * whatever a brand sets — the series, status, RF and sequential colours, the
 * danger colour, the radii, the spacing and the shadows (decision 2a) — and
 * brand.test.tsx holds this list to that. */
export const BRAND_TOKENS: readonly string[] = [
  ...ACCENT_TOKENS,
  ...FACE_TOKENS,
  ...Object.values(THEMES).flatMap((suffix) => Object.values(PALETTE_ROLES).map((t) => t + suffix)),
];

/** Which resolution a brand's files are read through. `"me"` is the caller's,
 * from their memberships; `"host"` is the one the host wears before a session.
 * Neither path names an organization. */
export type BrandSource = "me" | "host";

/** The path a brand file is read at — never an organization's path, which a
 * person beneath one may not read (§6.3).
 *
 * The four templates are written out whole rather than assembled from parts.
 * `tools/uiclientcheck` proves every path this application calls is declared
 * in `api/openapi.yaml`, and it reads the source: a path built by
 * concatenation is a path it cannot see, so it would report this file as
 * calling something undocumented while the call itself was fine. Spelling
 * them out is what keeps that check able to answer. */
export type AssetKind = "logo" | "favicon" | "logo_dark";

const ASSET_PATHS: Record<BrandSource, Record<AssetKind, string>> = {
  host: { logo: "/v1/brand/logo/{digest}", favicon: "/v1/brand/favicon/{digest}", logo_dark: "/v1/brand/logo_dark/{digest}" },
  me: { logo: "/v1/me/brand/logo/{digest}", favicon: "/v1/me/brand/favicon/{digest}", logo_dark: "/v1/me/brand/logo_dark/{digest}" },
};

export function assetPath(kind: AssetKind, digest: string, source: BrandSource = "me"): string {
  return ASSET_PATHS[source][kind].replace("{digest}", digest);
}

const ORG_ASSET_PATHS: Record<AssetKind, string> = {
  logo: "/v1/orgs/{id}/logo/{digest}",
  favicon: "/v1/orgs/{id}/favicon/{digest}",
  logo_dark: "/v1/orgs/{id}/logo_dark/{digest}",
};

/** The path an organization's own file is read at, for the page that sets it. */
export function orgAssetPath(kind: AssetKind, orgId: string, digest: string): string {
  return ORG_ASSET_PATHS[kind].replace("{id}", encodeURIComponent(orgId)).replace("{digest}", digest);
}

function rgb(hex: string): [number, number, number] | undefined {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return undefined;
  return [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)];
}

/** The ink drawn on an accent: white on a dark one, near-black on a light one,
 * decided by the accent's relative luminance so a reseller's colour keeps its
 * text readable in both themes without a second field to fill. */
export function inkFor(hex: string): string {
  const c = rgb(hex);
  if (!c) return "#ffffff";
  const lin = (v: number) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const l = 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
  // Contrast against white is (1.05 / (l + 0.05)); against near-black (l + 0.05) / 0.05.
  return 1.05 / (l + 0.05) >= (l + 0.05) / 0.05 ? "#ffffff" : "#0b0f14";
}

/** The accent's soft form: the same hue at low alpha, for active navigation. */
export function softFor(hex: string): string {
  const c = rgb(hex);
  if (!c) return "";
  return `rgba(${c[0]}, ${c[1]}, ${c[2]}, 0.12)`;
}

/** A token's value for the pair: the light value alone when the brand set
 * one accent — what every brand before #429 did — or `light-dark()` of the
 * two, which follows `color-scheme` the way theme.css's own tokens do, so a
 * theme switch needs no listener here. */
function pair(light: string, dark: string | undefined): string {
  return dark ? `light-dark(${light}, ${dark})` : light;
}

/** Puts a brand on the document, or takes it off when given nothing.
 *
 * `label` is what this deployment calls itself (#963), drawn in front of the
 * name. It is passed in rather than read here so this stays a function of its
 * arguments: a test can set the title for any deployment without standing up a
 * request, and there is no module state deciding what the title says. Empty —
 * the default — is production, and is also every page before the read lands. */
export function applyBrand(brand: Brand | undefined, source: BrandSource = "me", label: DeploymentLabel = ""): void {
  const root = document.documentElement;
  document.title = titleWith(label, brand?.display_name ?? SIGN_IN_TITLE);
  const accent = brand?.accent && rgb(brand.accent) ? brand.accent : undefined;
  // The dark accent is worn only beside a light one it can pair with (#429):
  // a brand that set accent_dark alone has nothing for the light theme, and
  // the product's own accent in both themes is the honest reading of that.
  const dark = accent && brand?.accent_dark && rgb(brand.accent_dark) ? brand.accent_dark : undefined;
  const values: Record<(typeof ACCENT_TOKENS)[number], string | undefined> = {
    "--accent": accent && pair(accent, dark),
    "--accent-ink": accent && pair(inkFor(accent), dark && inkFor(dark)),
    "--accent-soft": accent && pair(softFor(accent), dark && softFor(dark)),
  };
  for (const token of ACCENT_TOKENS) {
    const v = values[token];
    if (v) root.style.setProperty(token, v); else root.style.removeProperty(token);
  }
  // The typeface pair (ADR-0242 decision 1a): a key the list holds sets its
  // stack, anything else leaves the product's face.
  const faces: Record<(typeof FACE_TOKENS)[number], string | undefined> = {
    "--font": brand?.face ? FACE_STACKS[brand.face] : undefined,
    "--mono": brand?.face_mono ? MONO_STACKS[brand.face_mono] : undefined,
  };
  for (const token of FACE_TOKENS) {
    const v = faces[token];
    if (v) root.style.setProperty(token, v); else root.style.removeProperty(token);
  }
  // The palette (decision 2a): a theme the brand set writes its five base
  // halves and is marked, so theme.css derives the rest of that theme from
  // them; a theme it did not set wears the product's own. A theme with a
  // colour that does not parse is not worn at all — five or none.
  for (const [theme, suffix] of Object.entries(THEMES) as [keyof typeof THEMES, string][]) {
    const set = brand?.palette?.[theme];
    const whole = set && Object.keys(PALETTE_ROLES).every((role) => rgb(set[role as keyof typeof PALETTE_ROLES]));
    for (const [role, token] of Object.entries(PALETTE_ROLES)) {
      if (whole) root.style.setProperty(token + suffix, set[role as keyof typeof PALETTE_ROLES]);
      else root.style.removeProperty(token + suffix);
    }
    if (whole) root.setAttribute(`data-brand-${theme}`, ""); else root.removeAttribute(`data-brand-${theme}`);
  }
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (brand?.favicon_digest) {
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.type = "image/png";
    link.href = assetPath("favicon", brand.favicon_digest, source);
  } else {
    link?.remove();
  }
}

/** Wears the brand while the shell is mounted; the sign-in page that follows
 * a sign-out is neutral again.
 *
 * The deployment's marker is read here rather than by every caller, so a page
 * that wears a brand is marked by having done so. The cleanup carries it too:
 * signing out returns the title to the neutral one, and on staging the neutral
 * one is still marked — a sign-in page is as easy to mistake for production's
 * as any other, and it is the page somebody is about to type a password into. */
export function useBrand(brand: Brand | undefined, source: BrandSource = "me"): void {
  const label = useDeploymentLabel();
  useEffect(() => { applyBrand(brand, source, label); return () => applyBrand(undefined, "me", label); }, [brand, source, label]);
}

/** Above this width-to-height ratio a logo is a wordmark: it says the name
 * itself, so the text beside it is for screen readers only (#429). A square
 * mark keeps the name visible. Velocity's is 130×49, 2.65. */
export const WORDMARK_RATIO = 1.6;

/** Whether an image, once loaded, is a wordmark by its natural size. */
export function isWordmark(img: { naturalWidth: number; naturalHeight: number }): boolean {
  return img.naturalHeight > 0 && img.naturalWidth / img.naturalHeight > WORDMARK_RATIO;
}

/** The mark beside the name: the logo when there is one, else the name's
 * first letter on the accent. `onShape` says, once the logo has loaded,
 * whether it turned out to be a wordmark. A logo the brand wants white on
 * the dark theme carries the class theme.css inverts under `[data-theme]`. */
export function BrandMark({ brand, onShape, source = "me" }: { brand: Brand; onShape?: (wordmark: boolean) => void; source?: BrandSource }) {
  const ref = useRef<HTMLImageElement>(null);
  const [wordmark, setWordmark] = useState(false);
  const shape = (img: { naturalWidth: number; naturalHeight: number }) => { const w = isWordmark(img); setWordmark(w); onShape?.(w); };
  // A logo the browser already holds can be complete before the load
  // handler is attached; its shape is read on mount as well as on load.
  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth > 0) shape(img);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shape is a closure over onShape, re-read per digest
  }, [brand.logo_digest]);
  if (brand.logo_digest) {
    // A second file for the dark theme (ADR-0242 decision 5a) is drawn there
    // in place of the logo, and logo_on_dark is then not consulted; theme.css
    // shows one of the pair by the theme in force.
    const dark = brand.logo_dark_digest;
    const cls = "brand-logo" + (wordmark ? " wordmark" : "") + (dark ? " light-only" : brand.logo_on_dark === "white" ? " on-dark-white" : "");
    const light = <img ref={ref} className={cls} src={assetPath("logo", brand.logo_digest, source)} alt="" onLoad={(e) => shape(e.currentTarget)} />;
    if (!dark) return light;
    return (
      <>
        {light}
        <img className={"brand-logo dark-only" + (wordmark ? " wordmark" : "")} src={assetPath("logo_dark", dark, source)} alt="" />
      </>
    );
  }
  return <span className="brand-mark" aria-hidden="true">{(brand.display_name.trim()[0] ?? "?").toUpperCase()}</span>;
}

/** The mark and the name together, as the navigation's first link draws
 * them. A wordmark replaces the visible name rather than sitting beside a
 * second copy of it; the name stays in the accessible tree either way. */
export function BrandLockup({ brand, source = "me" }: { brand: Brand; source?: BrandSource }) {
  const [wordmark, setWordmark] = useState(false);
  return (
    <>
      <BrandMark brand={brand} onShape={setWordmark} source={source} />
      <span className={wordmark ? "sr-only" : undefined}>{brand.display_name}</span>
    </>
  );
}

/** What sits at the foot of the navigation: where to turn for help, the
 * legal footer, and the "powered by" line unless the organization switched
 * it off. */
export function BrandFoot({ brand }: { brand: Brand }) {
  const s = brand.support;
  const l = brand.legal;
  const hasSupport = !!(s?.email || s?.url || s?.phone);
  const hasLegal = !!(l?.text || l?.url);
  if (!hasSupport && !hasLegal && !brand.powered_by) return null;
  return (
    <div className="brand-foot">
      {hasSupport && (
        <div className="brand-support">
          <span className="label">Support</span>
          {s?.email && <a href={`mailto:${s.email}`}>{s.email}</a>}
          {s?.url && <a href={s.url} target="_blank" rel="noreferrer">Help center</a>}
          {s?.phone && <span>{s.phone}</span>}
        </div>
      )}
      {hasLegal && (
        <div className="brand-legal">
          {l?.text && <span>{l.text}</span>}
          {l?.url && <a href={l.url} target="_blank" rel="noreferrer">Terms and privacy</a>}
        </div>
      )}
      {brand.powered_by && <div className="powered">Powered by arpIQ</div>}
    </div>
  );
}
